import { LogoUploader } from "~/components/locations/logo-uploader";
import { ImageDropzone } from "~/components/shared/image-dropzone";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { Textarea } from "~/components/ui/textarea";

import { SettingsSection } from "./form-fields";
import type { SectionProps } from "./types";
import { FONT_OPTIONS } from "./types";

export function BrandingSection({
  form,
  logoUrl,
  bannerUrl,
  faviconUrl,
  ogImageUrl,
  onUpdate,
}: SectionProps & {
  logoUrl: string | null | undefined;
  bannerUrl: string | null | undefined;
  faviconUrl: string | null | undefined;
  ogImageUrl: string | null | undefined;
}) {
  return (
    <SettingsSection
      description="Customize the look of your storefront"
      title="Branding"
    >
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="logoStorageId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Logo</FormLabel>
              <FormControl>
                <LogoUploader
                  onValueChange={(val) => {
                    field.onChange(val);
                    setTimeout(onUpdate, 0);
                  }}
                  photoName="storefront logo"
                  value={logoUrl ?? null}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="bannerStorageId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Banner Image</FormLabel>
              <FormControl>
                <ImageDropzone
                  onValueChange={(val) => {
                    field.onChange(val);
                    setTimeout(onUpdate, 0);
                  }}
                  value={bannerUrl ?? field.value}
                />
              </FormControl>
              <p className="text-muted-foreground text-xs">
                Recommended: 1200x400
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="faviconStorageId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Favicon</FormLabel>
              <FormControl>
                <LogoUploader
                  onValueChange={(val) => {
                    field.onChange(val);
                    setTimeout(onUpdate, 0);
                  }}
                  photoName="favicon"
                  value={faviconUrl ?? null}
                />
              </FormControl>
              <p className="text-muted-foreground text-xs">32x32 recommended</p>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="ogImageStorageId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Social Preview Image</FormLabel>
              <FormControl>
                <ImageDropzone
                  onValueChange={(val) => {
                    field.onChange(val);
                    setTimeout(onUpdate, 0);
                  }}
                  value={ogImageUrl ?? field.value}
                />
              </FormControl>
              <p className="text-muted-foreground text-xs">
                1200x630 recommended (shown when shared on social media)
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="accentColor"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Accent Color</FormLabel>
              <FormControl>
                <div className="flex items-center gap-2">
                  <input
                    className="h-10 w-12 cursor-pointer rounded border p-1"
                    onBlur={() => onUpdate()}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                    type="color"
                    value={field.value}
                  />
                  <Input
                    className="w-32"
                    onBlur={() => onUpdate()}
                    onChange={(e) => field.onChange(e.target.value)}
                    placeholder="#000000"
                    value={field.value}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="secondaryColor"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Secondary Color</FormLabel>
              <FormControl>
                <div className="flex items-center gap-2">
                  <input
                    className="h-10 w-12 cursor-pointer rounded border p-1"
                    onBlur={() => onUpdate()}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                    type="color"
                    value={field.value || "#f0f0f0"}
                  />
                  <Input
                    className="w-32"
                    onBlur={() => onUpdate()}
                    onChange={(e) => field.onChange(e.target.value)}
                    placeholder="#f0f0f0"
                    value={field.value}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="fontFamily"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Font</FormLabel>
              <FormControl>
                <select
                  className="border-input flex h-9 w-full max-w-xs rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm"
                  onBlur={() => onUpdate()}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    onUpdate();
                  }}
                  value={field.value}
                >
                  {FONT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="welcomeMessage"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Welcome Message</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  onBlur={() => onUpdate()}
                  placeholder="Welcome to our restaurant! Order online for pickup or delivery."
                  rows={3}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </SettingsSection>
  );
}
